import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { WizardLayout } from "@/components/we360/WizardLayout";
import DemoFormOverlay from "@/components/DemoFormOverlay";
import { useFormValidation } from "@/hooks/useFormValidation";
import { CheckCircle } from "lucide-react";
import { syncF4 } from "@/services/backendSync";
import Step1Consumption1 from "./Step1Consumption1";
import Step2Consumption2 from "./Step2Consumption2";
import Step3Behaviors from "./Step3Behaviors";
import Step4Hydration from "./Step4Hydration";
import Step5Motivation from "./Step5Motivation";
import Step6Summary from "./Step6Summary";
import {
  FOOD_ITEMS, BEHAVIOR_ITEMS, FREQ_DIET, FREQ_BEHAVIOR,
  scoreItem, classifyDiet, classifyBehavior, getHydrationCategory,
} from "./helpers";

const STEPS = [
  { id: "consumption1", title: "Consumo de Alimentos", sub: "Ítems 1-5" },
  { id: "consumption2", title: "Consumo de Alimentos", sub: "Ítems 6-10" },
  { id: "behaviors", title: "Conductas Alimentarias", sub: "Hábitos diarios" },
  { id: "hydration", title: "Hidratación", sub: "Consumo de agua" },
  { id: "motivation", title: "Motivación y Restricciones", sub: "Objetivos" },
  { id: "summary", title: "Confirmación", sub: "Resumen final" },
];

function getStepRules(step, foodAnswers, behaviorAnswers) {
  switch (step) {
    case 0: {
      const rules = {};
      FOOD_ITEMS.slice(0, 5).forEach((item) => {
        rules[`food_${item.id}`] = { required: true, message: "Selecciona una frecuencia" };
      });
      return rules;
    }
    case 1: {
      const rules = {};
      FOOD_ITEMS.slice(5).forEach((item) => {
        rules[`food_${item.id}`] = { required: true, message: "Selecciona una frecuencia" };
      });
      return rules;
    }
    case 2: {
      const rules = {};
      BEHAVIOR_ITEMS.forEach((item) => {
        rules[`behavior_${item.id}`] = { required: true, message: "Selecciona una frecuencia" };
      });
      return rules;
    }
    case 3: return {
      hydration: { required: true, message: "Selecciona tu consumo de agua" },
    };
    case 4: return {
      motivaciones: { required: true, custom: (v) => (!Array.isArray(v) || v.length === 0) ? "Selecciona al menos una motivación" : null },
      nivelMotivacion: { required: true, message: "Selecciona tu nivel de motivación" },
    };
    case 5: return {}; // Summary - no validation
    default: return {};
  }
}

export default function FormF4() {
  const { profile, setProfile, isDemo } = useUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [fade, setFade] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { validateStep, getError, isStepValid, clearErrors, onFieldChange } = useFormValidation();

  const [foodAnswers, setFoodAnswers] = useState({});
  const [behaviorAnswers, setBehaviorAnswers] = useState({});
  const [hydration, setHydration] = useState("");
  const [motivaciones, setMotivaciones] = useState([]);
  const [nivelMotivacion, setNivelMotivacion] = useState("");
  const [restricciones, setRestricciones] = useState([]);
  const [restriccionOtra, setRestriccionOtra] = useState("");
  const [alergias, setAlergias] = useState("");

  const rules = useMemo(() => getStepRules(step, foodAnswers, behaviorAnswers), [step, foodAnswers, behaviorAnswers]);

  const setFoodAnswer = useCallback((id, val) => {
    setFoodAnswers((p) => ({ ...p, [id]: val }));
    const rule = rules[`food_${id}`];
    if (rule) onFieldChange(`food_${id}`, val, rule);
  }, [rules, onFieldChange]);
  const setBehaviorAnswer = useCallback((id, val) => {
    setBehaviorAnswers((p) => ({ ...p, [id]: val }));
    const rule = rules[`behavior_${id}`];
    if (rule) onFieldChange(`behavior_${id}`, val, rule);
  }, [rules, onFieldChange]);

  const toggleMotivacion = useCallback((g) => {
    setMotivaciones((p) => p.includes(g) ? p.filter((x) => x !== g) : [...p, g]);
  }, []);
  const toggleRestriccion = useCallback((r) => {
    setRestricciones((p) => p.includes(r) ? p.filter((x) => x !== r) : [...p, r]);
  }, []);

  // Computed scores
  const allFoodAnswered = FOOD_ITEMS.every((item) => foodAnswers[item.id] !== undefined && foodAnswers[item.id] !== "");
  const dietScore = allFoodAnswered
    ? FOOD_ITEMS.reduce((sum, item) => {
        const idx = FREQ_DIET.indexOf(foodAnswers[item.id]);
        return sum + (scoreItem(idx, item.inverse, 4) ?? 0);
      }, 0)
    : 0;

  const allBehaviorAnswered = BEHAVIOR_ITEMS.every((item) => behaviorAnswers[item.id] !== undefined && behaviorAnswers[item.id] !== "");
  const behaviorScore = allBehaviorAnswered
    ? BEHAVIOR_ITEMS.reduce((sum, item) => {
        const idx = FREQ_BEHAVIOR.indexOf(behaviorAnswers[item.id]);
        return sum + (scoreItem(idx, item.inverse, 3) ?? 0);
      }, 0)
    : 0;

  const hydrationCat = getHydrationCategory(hydration);

  // Build flat data for validation
  const flatData = useMemo(() => {
    const d = { hydration, motivaciones, nivelMotivacion };
    Object.entries(foodAnswers).forEach(([k, v]) => { d[`food_${k}`] = v; });
    Object.entries(behaviorAnswers).forEach(([k, v]) => { d[`behavior_${k}`] = v; });
    return d;
  }, [foodAnswers, behaviorAnswers, hydration, motivaciones, nivelMotivacion]);

  // Navigation
  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => { setStep((s) => s + dir); setFade(true); }, 200);
  }, []);

  const goNext = useCallback(() => {
    if (isDemo) { go(1); return; }
    const errs = validateStep(rules, flatData);
    if (Object.keys(errs).length > 0) return;
    go(1);
  }, [step, rules, flatData, validateStep, go, isDemo]);

  const goBack = useCallback(() => { clearErrors(); go(-1); }, [go, clearErrors]);

  const nextDisabled = !isDemo && !isStepValid(rules, flatData);

  // Submit
  const handleSubmit = useCallback(async () => {
    if (isDemo) return;
    const now = new Date().toISOString();
    const consumo = FOOD_ITEMS.map((item) => ({
      alimento: item.label,
      frecuencia: foodAnswers[item.id] || "",
      score: scoreItem(FREQ_DIET.indexOf(foodAnswers[item.id]), item.inverse, 4) ?? 0,
    }));
    const conductas = BEHAVIOR_ITEMS.map((item) => ({
      pregunta: item.label,
      frecuencia: behaviorAnswers[item.id] || "",
      score: scoreItem(FREQ_BEHAVIOR.indexOf(behaviorAnswers[item.id]), item.inverse, 3) ?? 0,
    }));

    const f4Results = {
      consumo,
      scoreCalidadDieta: dietScore,
      categoriaCalidadDieta: classifyDiet(dietScore).key,
      conductas,
      scoreConductas: behaviorScore,
      categoriaConductas: classifyBehavior(behaviorScore).key,
      hidratacion: hydrationCat?.key || "unknown",
      motivaciones,
      nivelMotivacion,
      restricciones,
      restriccionOtra,
      alergias,
      createdAt: now,
      completedAt: now,
    };
    setSubmitted(true);
    await setProfile((prev) => ({
      ...prev,
      f4Results,
      fhirSyncStatus: "PENDING_FHIR",
      pendingFhirF4: true,
    }));

    const email = user?.email || profile.email;
    if (email) {
      syncF4(f4Results, email).then((res) => {
        if (res.ok) {
          console.log("[F4] Backend sync OK:", res.data);
          setProfile((prev) => ({
            ...prev,
            pendingFhirF4: false,
            fhirSyncStatus: "SYNCED",
            fhirSyncedAt: new Date().toISOString(),
          }));
        } else {
          console.warn("[F4] Backend sync failed (queued in Outbox):", res);
        }
      });
    }

    setTimeout(() => navigate("/dashboard"), 2000);
  }, [foodAnswers, behaviorAnswers, dietScore, behaviorScore, hydrationCat, motivaciones, nivelMotivacion, restricciones, restriccionOtra, alergias, setProfile, navigate, isDemo, user, profile.email]);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-8 text-center max-w-[380px] w-full" style={{ boxShadow: "0 8px 30px rgba(45,42,36,0.10)" }} data-testid="f4-success-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#E2F0E8" }}>
            <CheckCircle size={32} color="#157346" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D2A24" }}>¡Evaluación nutricional completada!</h2>
          <p className="text-sm" style={{ color: "#71644C" }}>Redirigiendo a tu dashboard...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const errProps = { errors: { getError } };
    switch (step) {
      case 0: return <Step1Consumption1 answers={foodAnswers} onChange={setFoodAnswer} {...errProps} />;
      case 1: return <Step2Consumption2 answers={foodAnswers} onChange={setFoodAnswer} allAnswered={allFoodAnswered} dietScore={dietScore} {...errProps} />;
      case 2: return <Step3Behaviors answers={behaviorAnswers} onChange={setBehaviorAnswer} allAnswered={allBehaviorAnswered} behaviorScore={behaviorScore} {...errProps} />;
      case 3: return <Step4Hydration value={hydration} onChange={setHydration} hydrationCat={hydrationCat} {...errProps} />;
      case 4: return (
        <Step5Motivation
          motivaciones={motivaciones} onToggleMotivacion={toggleMotivacion}
          nivelMotivacion={nivelMotivacion} onNivelChange={setNivelMotivacion}
          restricciones={restricciones} onToggleRestriccion={toggleRestriccion}
          restriccionOtra={restriccionOtra} onRestriccionOtraChange={setRestriccionOtra}
          alergias={alergias} onAlergiasChange={setAlergias}
          {...errProps}
        />
      );
      case 5: return (
        <Step6Summary
          dietScore={dietScore} behaviorScore={behaviorScore} hydrationCat={hydrationCat}
          nivelMotivacion={nivelMotivacion} motivaciones={motivaciones}
          restricciones={restricciones} restriccionOtra={restriccionOtra}
        />
      );
      default: return null;
    }
  };

  return (
    <DemoFormOverlay>
      <WizardLayout
        steps={STEPS}
        currentStep={step}
        onNext={goNext}
        onBack={goBack}
        fade={fade}
        userName={profile.nombres}
        isLastStep={step === 5}
        onSubmit={handleSubmit}
        submitDisabled={isDemo}
        nextDisabled={nextDisabled}
        nextLabel={step === 0 ? "Comenzar evaluación \u2192" : undefined}
        submitLabel="Enviar evaluación"
      >
        {renderStep()}
      </WizardLayout>
    </DemoFormOverlay>
  );
}
